import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  MenuItem,
  TextField,
  useTheme,
  Button,
  Tooltip,
  Checkbox,
  Popover,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { getFormattedDate } from "utils/time.util";
import {
  setCollectionId,
  setSelectedBookmarkOption,
} from "context/features/dataSlice";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { logEvent } from "services/amplitude.service";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import { useSourceSearch } from "features/SourceSearch";
import AccessibleIconButton from "components/base/AccessibleIconButton";
// import getRefreshTOkenWashingtonPost from "hooks/use-washinghton-token";

const Header = ({
  toggleSidebar = null,
  selectedBookmarkOption,
  edit,
  setEdit,
  saveNewCollection,
  setSelectedCollection,
  selectedCollection,
  toggleBookmarkCollection,
  saveBookmarkToCollections,
  setDeletePopup,
  create,
  setCreate,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [collectionAnchorEl, setCollectionAnchorEl] = useState(null);
  const [bookmarkTitle, setBookmarkTitle] = useState("");
  const dispatch = useDispatch();
  const [disabled, setDisabled] = useState(false);
  const [pendingCollectionIds, setPendingCollectionIds] = useState([]);
  const [applying, setApplying] = useState(false);

  const theme = useTheme();
  const navbarBackground = theme.palette.secondary.main;

  const {
    summaryInfoData,
    collections,
    collectionSourceId,
    collectionId,
    parentCollections,
    archivedCollections,
    bookmarkedCollectionIds,
    isReadOnlyView,
  } = useSelector((state) => state.data);

  const isFullyArchived =
    bookmarkedCollectionIds.length > 0 &&
    bookmarkedCollectionIds.every((id) =>
      archivedCollections.some(
        (collection) => Number(collection.collectionId) === Number(id)
      )
    );

  const currentArchivedCollection = isFullyArchived
    ? archivedCollections.find((collection) =>
        bookmarkedCollectionIds.includes(Number(collection.collectionId))
      )
    : null;

  // Submissions are read-only for the same reason archived collections are.
  const readOnly = isReadOnlyView || isFullyArchived;

  // Deleting only makes sense when "the collection currently being viewed" is
  // well-defined, i.e. from inside Collections itself — not from Pulse/Search,
  // where there's no specific collection to scope the removal to.
  const { pathname } = useLocation();
  const isCollectionsPage = pathname.startsWith("/collections");

  const DROPDOWN_LABEL_MAX_LENGTH = 20;

  const dropdownLabel = () => {
    const selectedNames = collections
      .filter((c) => bookmarkedCollectionIds.includes(c.collectionId))
      .map((c) => c.name)
      .sort((a, b) => a.localeCompare(b));
    if (selectedNames.length === 0) return "";
    const firstName =
      selectedNames[0].length > DROPDOWN_LABEL_MAX_LENGTH
        ? `${selectedNames[0].slice(0, DROPDOWN_LABEL_MAX_LENGTH)}...`
        : selectedNames[0];
    if (selectedNames.length === 1) return firstName;
    return `${firstName} + ${selectedNames.length - 1}`;
  };

  const { currentCategory, currentSearchTerm } = useSourceSearch();

  const handleSelectBookmarkOption = (option) => {
    dispatch(setSelectedBookmarkOption(option));
    logEvent(`click_${option}_option`, { click: true });
  };

  const handleParentSelect = (event) => {
    dispatch(setCollectionId(event.target.value));
    setSelectedCollection(event.target.value);
  };

  const handleTogglePendingCollection = (collectionIdToToggle) => {
    setPendingCollectionIds((prev) =>
      prev.includes(collectionIdToToggle)
        ? prev.filter((cid) => cid !== collectionIdToToggle)
        : [...prev, collectionIdToToggle]
    );
  };

  const handleOpenDropdown = (event) => {
    setPendingCollectionIds(bookmarkedCollectionIds);
    setCollectionAnchorEl(event.currentTarget);
  };

  const handleCloseDropdown = () => {
    setCollectionAnchorEl(null);
    setPendingCollectionIds(bookmarkedCollectionIds);
  };

  const handleApplyCollections = async () => {
    setApplying(true);
    try {
      await saveBookmarkToCollections(pendingCollectionIds);
      toast.success("Bookmark saved to collection successfully!");
      setCollectionAnchorEl(null);
    } catch (error) {
      toast.error("Something went wrong, please try later.");
    } finally {
      setApplying(false);
    }
  };

  const handleStartCreate = () => {
    setBookmarkTitle("");
    setCreate(true);
    setCollectionAnchorEl(null);
  };

  const handleSaveNewCollection = async () => {
    if (disabled) return;
    try {
      if (bookmarkTitle.length === 0) {
        toast.error("Collection name cannot be empty!");
        return;
      }
      setDisabled(true);
      const id = await saveNewCollection(bookmarkTitle);
      dispatch(setCollectionId(id));
      setCreate(false);
      await toggleBookmarkCollection(id, true);
      toast.success("Bookmark saved to collection successfully!");
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setTimeout(() => {
        setDisabled(false);
      }, 1000);
    }
  };

  const TopHeader = () => {
    return (
      <Stack
        direction="row"
        p={2}
        justifyContent="space-between"
        alignItems="center"
      >
        {!edit ? (
          <Typography
            variant="h5"
            gutterBottom
            sx={{ color: "#696b6c", fontSize: "2rem", mb: 0 }}
          >
            Details
          </Typography>
        ) : (
          <Typography
            onClick={() => logEvent("click_edit_bookmark", { click: true })}
            variant="h5"
            gutterBottom
            sx={{ color: "#696b6c", fontSize: "2rem", mb: 0 }}
          >
            Edit Bookmark
          </Typography>
        )}

        <Stack direction="row" spacing={0.5}>
          {collectionSourceId && !currentArchivedCollection && !readOnly && (
            <>
              {!edit && (
                <AccessibleIconButton
                  title="Edit"
                  onClick={() => setEdit(true)}
                  color="inherit"
                >
                  <EditOutlinedIcon
                    sx={{ color: "#a9baca", height: "2.2rem" }}
                  />
                </AccessibleIconButton>
              )}
              {isCollectionsPage && (
                <AccessibleIconButton
                  title="Delete"
                  onClick={() => setDeletePopup(true)}
                >
                  <DeleteOutlineIcon
                    sx={{ color: "#a9baca", height: "2.2rem" }}
                  />
                </AccessibleIconButton>
              )}
            </>
          )}
          {toggleSidebar != null && (
              <AccessibleIconButton
                color="inherit"
                title="Close"
                edge="start"
                onClick={toggleSidebar}
              >
                <CloseIcon sx={{ color: "#a9baca", height: "2.2rem" }} />
              </AccessibleIconButton>
          )}
        </Stack>
      </Stack>
    );
  };

  const BookmarkOptions = () => {
    const getBookmarkSxProps = (option) => ({
      fontStyle: "italic",
      cursor: "pointer",
      textDecoration: selectedBookmarkOption === option ? "underline" : "none",
      textDecorationThickness: "2px",
      textUnderlineOffset: "8px",
      color:
        selectedBookmarkOption === option
          ? theme.palette.primary.main
          : "#696b6c",
    });

    return (
      <Stack mt={2} mb="8px" direction="row" justifyContent="center" gap={3}>
        <Typography
          variant="h6"
          sx={getBookmarkSxProps("notes")}
          onClick={() => {
            handleSelectBookmarkOption("notes");
          }}
        >
          Notes
        </Typography>
        <Typography
          variant="h6"
          sx={getBookmarkSxProps("citation")}
          onClick={() => handleSelectBookmarkOption("citation")}
        >
          Citation
        </Typography>
        {/* <Typography
          variant="h6"
          sx={getBookmarkSxProps("summary")}
          onClick={() => handleSelectBookmarkOption("summary")}
        >
          Summary
        </Typography> */}
        <Typography
          variant="h6"
          sx={getBookmarkSxProps("readinglevel")}
          onClick={() => handleSelectBookmarkOption("readinglevel")}
        >
          Reading Levels
        </Typography>
      </Stack>
    );
  };

  useEffect(() => {
    if (
      collectionId &&
      collectionSourceId &&
      (collections.length || currentArchivedCollection)
    ) {
      setSelectedCollection(collectionId);
    }
  }, [collectionId, collections, currentArchivedCollection]);

  useEffect(() => {
    if (!collectionAnchorEl) {
      setPendingCollectionIds(bookmarkedCollectionIds);
    }
  }, [bookmarkedCollectionIds]);

  // useEffect(() => {
  //   const iframe = document.createElement("iframe");
  //   iframe.src = summaryInfoData.url;
  //   iframe.style =
  //     "border: none !important; margin: 0px !important; padding: 0px !important; width: 1px !important; min-width: 100% !important; overflow: hidden !important; display: block !important; visibility: hidden !important; position: fixed !important; height: 1px !important; pointer-events: none !important; user-select: none !important;";

  //   document.body.appendChild(iframe);

  //   return () => {
  //     document.body.removeChild(iframe);
  //   };
  // }, [summaryInfoData]);

  return (
    <Box sx={{ backgroundColor: navbarBackground }}>
      <TopHeader />
      {!edit && (
        <React.Fragment>
          <Stack px={5} direction="column" gap={1}>
            <Tooltip title={summaryInfoData.title}>
              <Typography noWrap={true} variant="body4">
                {summaryInfoData.title}
              </Typography>
            </Tooltip>
            <Tooltip title={summaryInfoData.url}>
              <SearchResultLink
                url={summaryInfoData.url}
                trackingCat="click_resultset_title"
                trackingOptions={{
                  url: summaryInfoData.url,
                  query:
                    currentSearchTerm != null &&
                    currentSearchTerm.trim().length > 0
                      ? currentSearchTerm
                      : undefined,
                  entity: summaryInfoData.publisher,
                  category:
                    currentCategory != null && currentCategory.trim().length > 0
                      ? currentCategory
                      : undefined,
                  type: "bookmark sidebar",
                  title: summaryInfoData.title,
                  date_published: summaryInfoData.utc_date_published,
                }}
                variant="body3"
                color="grey.600"
                noWrap
                sx={{
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                  cursor: "pointer",
                }}
              >
                {summaryInfoData.url}
              </SearchResultLink>
            </Tooltip>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography
                variant="body2"
                color={theme.palette.grey[700]}
                fontWeight="bold"
              >
                {summaryInfoData.publisher}
              </Typography>
              <Typography variant="body2" color="grey.600">
                {getFormattedDate(summaryInfoData.published_date)}
              </Typography>
            </Stack>
            {create && (
              <TextField
                label="Enter new Collection name"
                value={bookmarkTitle}
                onChange={(e) => setBookmarkTitle(e.target.value)}
              ></TextField>
            )}
            {readOnly && !currentArchivedCollection ? null : currentArchivedCollection ? (
              <TextField
                select
                label="Archived Collection"
                value={currentArchivedCollection.collectionId}
                variant="outlined"
                disabled
                sx={{ mt: 1.5 }}
              >
                <MenuItem value={currentArchivedCollection.collectionId}>
                  {currentArchivedCollection.name}
                </MenuItem>
              </TextField>
            ) : create ? (
              <TextField
                select
                label="Creating a sub-folder? Pick parent Collection here"
                value={selectedCollection}
                onChange={handleParentSelect}
                variant="outlined"
                SelectProps={{
                  open: dropdownOpen,
                  onOpen: () => setDropdownOpen(true),
                  onClose: () => setDropdownOpen(false),
                  onClick: (event) => event.stopPropagation(),
                  MenuProps: {
                    PaperProps: { style: { maxHeight: 280, maxWidth: 300 } },
                  },
                }}
                sx={{ mt: 1.5 }}
              >
                {parentCollections.map((collection) => (
                  <MenuItem
                    key={collection.collectionId}
                    value={collection.collectionId}
                  >
                    {collection.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <React.Fragment>
                <TextField
                  label="Add to Collection..."
                  value={dropdownLabel()}
                  onClick={handleOpenDropdown}
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <ArrowDropDownIcon sx={{ color: "action.active" }} />
                    ),
                  }}
                  sx={{
                    mt: 1.5,
                    cursor: "pointer",
                    "& .MuiInputBase-root": { cursor: "pointer" },
                    "& .MuiInputBase-input": { cursor: "pointer" },
                  }}
                />
                <Popover
                  open={Boolean(collectionAnchorEl)}
                  anchorEl={collectionAnchorEl}
                  onClose={handleCloseDropdown}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  slotProps={{
                    paper: {
                      style: {
                        width: collectionAnchorEl?.offsetWidth,
                        maxWidth: "90vw",
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      maxHeight: 260,
                      overflowY: "auto",
                      overflowX: "hidden",
                      width: "100%",
                    }}
                  >
                    <MenuItem onClick={handleStartCreate} sx={{ py: 0.5 }}>
                      + Create New
                    </MenuItem>
                    {collections.map((collection) => (
                      <MenuItem
                        key={collection.collectionId}
                        onClick={() =>
                          handleTogglePendingCollection(
                            collection.collectionId
                          )
                        }
                        sx={{ py: 0.5, minWidth: 0 }}
                      >
                        <Checkbox
                          size="small"
                          sx={{ p: 0.5, mr: 0.5, flexShrink: 0 }}
                          checked={pendingCollectionIds.includes(
                            collection.collectionId
                          )}
                        />
                        <Typography noWrap>{collection.name}</Typography>
                      </MenuItem>
                    ))}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      borderTop: `1px solid ${theme.palette.divider}`,
                      p: 1,
                    }}
                  >
                    <Button
                      onClick={handleApplyCollections}
                      disabled={applying}
                      sx={{ textTransform: "none" }}
                    >
                      {applying ? "Saving..." : "OK"}
                    </Button>
                  </Box>
                </Popover>
              </React.Fragment>
            )}
            {create && (
              <Box
                sx={{
                  backgroundColor: navbarBackground,
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <Button
                  onClick={handleSaveNewCollection}
                  disabled={disabled}
                  sx={{
                    mt: 2,
                    textTransform: "none",
                    color: "#fff",
                  }}
                >
                  Save
                </Button>
              </Box>
            )}
          </Stack>

          <BookmarkOptions />
        </React.Fragment>
      )}
    </Box>
  );
};

export default Header;
